import React from 'react';
import Flag from 'react-flags';
import styles from './ContactPopup.module.css';
import { exists, formatAddress } from './utils';

/**
 * Renders the copy button
 */
export const CopyButton = ({ text, onCopy, t }) => {
    return (
        <button 
            className={styles.copyButton}
            onClick={() => onCopy(text)}
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
            {t('general.UI.copy')}
        </button>
    );
};

/**
 * Renders the contact methods section (email, phone)
 */
export const ContactMethodsSection = ({ talent, onCopy, t }) => {
    const hasContactInfo = exists(talent.email) || exists(talent.phone);
    
    if (!hasContactInfo) {
        return (
            <div className={styles.sectionGroup}>
                <h5 className={styles.sectionTitle}>{t('general.UI.contact_details')}</h5>
                <div className={styles.emptyInfoMessage}>
                    <p>{t('general.UI.no_data')}</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className={styles.sectionGroup}>
            <h5 className={styles.sectionTitle}>{t('general.UI.contact_details')}</h5>
            
            {exists(talent?.email) && (
                <div className={styles.infoRow}>
                    <span className={styles.infoIcon}>📧</span>
                    <div className={styles.infoContent}>
                        <span className={styles.infoLabel}>{t('general.UI.email')}</span>
                        <span className={styles.infoValue}>
                            <a href={`mailto:${talent.email}`}>{talent.email}</a>
                        </span>
                        <div className={styles.infoActions}>
                            <CopyButton text={talent.email} onCopy={onCopy} t={t} />
                        </div>
                    </div>
                </div>
            )}

            {exists(talent.phone) && (
                <div className={styles.infoRow}>
                    <span className={styles.infoIcon}>📱</span>
                    <div className={styles.infoContent}>
                        <span className={styles.infoLabel}>{t('general.UI.phone')}</span>
                        <span className={styles.infoValue}>
                            <a href={`tel:${talent.phone}`}>{talent.phone}</a>
                        </span>
                        <div className={styles.infoActions}>
                            <CopyButton text={talent.phone} onCopy={onCopy} t={t} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Renders the location section (addresses, country, nationality)
 */
export const LocationSection = ({ talent, t }) => {
    const hasCurrentAddress = exists(talent?.Address_Line_1) || exists(talent?.Address_Line_2) || 
                             exists(talent?.city) || exists(talent?.state) || exists(talent?.country);
                             
    const hasPermanentAddress = exists(talent?.permanent_address_line1) || exists(talent?.permanent_address_line2) || 
                               exists(talent?.permanent_city) || exists(talent?.permanent_state) || 
                               exists(talent?.permanent_country);
                               
    const hasLocation = hasCurrentAddress || hasPermanentAddress || 
                      exists(talent?.country) || exists(talent?.nationality);
    
    if (!hasLocation) {
        return (
            <div className={styles.sectionGroup}>
                <h5 className={styles.sectionTitle}>{t('general.UI.location')}</h5>
                <div className={styles.emptyInfoMessage}>
                    <p>{t('general.UI.no_data')}</p>
                </div>
            </div>
        );
    }
    
    return (
        <div className={styles.sectionGroup}>
            <h5 className={styles.sectionTitle}>{t('general.UI.location')}</h5>
            
            {/* Current Address */}
            {hasCurrentAddress && (
                <div className={styles.infoRow}>
                    <span className={styles.infoIcon}>📍</span>
                    <div className={styles.infoContent}>
                        <span className={styles.infoLabel}>{t('contactDetails.currentResidentAddress')}</span>
                        <span className={styles.infoValue}>
                            {formatAddress(
                                talent?.Address_Line_1||'', 
                                talent?.Address_Line_2||'', 
                                talent?.city||'', 
                                talent?.state||'', 
                                talent?.country||'',
                                t
                            ) || <span className={styles.noData}>{t('general.UI.no_data')}</span>}
                        </span>
                    </div>
                </div>
            )}
            
            {/* Permanent Address */}
            {hasPermanentAddress && (
                <div className={styles.infoRow}>
                    <span className={styles.infoIcon}>🏠</span>
                    <div className={styles.infoContent}>
                        <span className={styles.infoLabel}>{t('contactDetails.permanentAddress')}</span>
                        <span className={styles.infoValue}>
                            {formatAddress(
                                talent?.permanent_address_line1, 
                                talent?.permanent_address_line2, 
                                talent?.permanent_city, 
                                talent?.permanent_state, 
                                talent?.permanent_country,
                                t
                            ) || <span className={styles.noData}>{t('general.UI.no_data')}</span>}
                        </span>
                    </div>
                </div>
            )}
        
            {/* Country */}
            {exists(talent?.country) && (
                <div className={styles.infoRow}>
                    <span className={styles.infoIconFlag}>
                        <Flag
                            name={talent?.country}
                            format="svg"
                            shiny={false}
                            basePath="/vendor/flags"
                        />
                    </span>
                    <div className={styles.infoContent}>
                        <span className={styles.infoLabel}>{t('general.UI.country')}</span>
                        <span className={styles.infoValue}>
                            {t('general.country.' + talent?.country)}
                        </span>
                    </div>
                </div>
            )}
            
            {/* Nationality */}
            {exists(talent?.nationality) && (
                <div className={styles.infoRow}>
                    <span className={styles.infoIcon}>🌍</span>
                    <div className={styles.infoContent}>
                        <span className={styles.infoLabel}>{t('general.UI.nationality')}</span>
                        <span className={styles.infoValue}>
                            {t('general.nationality.' + talent?.nationality)}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * Renders visa information section
 */
export const VisaSection = ({ talent, t }) => {
    if (!exists(talent?.visa_status)) {
        return null;
    }
    
    return (
        <div className={styles.sectionGroup}>
            <h5 className={styles.sectionTitle}>{t('contactDetails.visaStatus')}</h5>
            <div className={styles.infoRow}>
                <span className={styles.infoIcon}>🛂</span>
                <div className={styles.infoContent}>
                    <span className={styles.infoLabel}>{t('contactDetails.visaStatus')}</span>
                    <span className={styles.infoValue}>
                        <div className={styles.visaInfo}>
                            <span className={styles.visaStatus}>{talent?.visa_status}</span>
                            
                            {exists(talent?.visa_type) && (
                                <span className={styles.visaType}>
                                    {talent?.visa_type === 'visitVisa' && t('contactDetails.visitVisa')}
                                    {talent?.visa_type === 'workVisa' && t('contactDetails.workVisa')}
                                    {talent?.visa_type === 'residentVisa' && t('contactDetails.residentVisa')}
                                    {!['visitVisa', 'workVisa', 'residentVisa'].includes(talent?.visa_type) && talent?.visa_type}
                                </span>
                            )}
                            
                            {exists(talent?.visa_expiry) && (
                                <span className={styles.visaExpiry}>
                                    {t('contactDetails.visaExpiry')}: {new Date(talent?.visa_expiry).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                    </span>
                </div>
            </div>
        </div>
    );
}; 